import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import {
  FulfillmentAdminClient,
  INBOUND_STATUS,
  inboundStatusLabel,
} from '../../../../infrastructure/FulfillmentAdminClient';
import type { InboundRequestDetail } from '../../../../infrastructure/FulfillmentAdminClient';
import { getAdminApiUrl, AdminApiDomain } from '../../../../config/ApiRouting';
import { ROUTES } from '../../../../routes/routes';
import { fulfillmentSourceLabel } from '@/utils/fulfillmentSource';
import { InboundCommentThread } from '@/pages/fulfillment/components/InboundCommentThread';

interface TallyTile {
  label: string;
  value: number;
}

const GENERIC_ERROR = 'The request failed. Please try again shortly.';

export default function FulfillmentInboundRequestDetailPage() {
  const { docId } = useParams<{ docId: string }>();
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);

  const [detail, setDetail] = useState<InboundRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!docId) {
      return;
    }
    setDetail(await client.getInboundRequest(docId));
  }, [client, docId]);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    loadDetail()
      .catch(() => {
        if (!ignore) {
          setActionError(GENERIC_ERROR);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [loadDetail]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" aria-label="Loading inbound request" />
      </div>
    );
  }
  if (!detail || !docId) {
    return (
      <Container fluid className="py-3">
        <Alert variant="danger">{actionError ?? 'Could not load the inbound requests.'}</Alert>
      </Container>
    );
  }

  const { doc, lines, tallies } = detail;
  // An older server may leave the thread out; it reads as an empty one.
  const comments = detail.comments ?? [];
  const waybills = [...new Set([...(doc.trackings ?? []), ...(doc.tracking ? [doc.tracking] : [])])];
  const waybillText = waybills.length > 0 ? waybills.join(', ') : (doc.invoiceNo ?? 'Unregistered');
  const tiles: TallyTile[] = [
    { label: 'Expected qty', value: tallies.expected },
    { label: 'Received qty', value: tallies.received },
    { label: 'Remaining', value: tallies.remaining },
    // Nothing owed and nothing wrong are different facts, so the surplus gets its own tile rather than a
    // floored zero. A server too old to send the tally is counted from the lines, the way the desk does it.
    {
      label: 'Over-count',
      value: tallies.over ?? lines.reduce((sum, line) => sum + Math.max(line.qtyReceived - line.qtyExpected, 0), 0),
    },
  ];

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">
            Inbound request <span className="font-monospace">{doc.inboundNo}</span>
          </h2>
          <div className="text-muted small">
            {fulfillmentSourceLabel(doc.source)} · requested by {doc.operator ?? '—'} · supplier {doc.supplier ?? '—'}
          </div>
          <div className="text-muted small">
            {/* Both fields are the same waybill at two capture points; the later PATCH is the fresher one. */}
            Waybill {waybillText} · expected arrival {doc.expectedAt ?? '—'}
            {doc.memo ? ` · ${doc.memo}` : ''}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Badge
            bg={doc.status === INBOUND_STATUS.RECEIVED ? 'success' : 'warning'}
            text={doc.status === INBOUND_STATUS.RECEIVED ? 'light' : 'dark'}
          >
            {inboundStatusLabel(doc.status)}
          </Badge>
          <Link to={ROUTES.fulfillment.inboundRequests} className="btn btn-outline-secondary btn-sm">
            Request list
          </Link>
        </div>
      </div>

      {actionError && <Alert variant="danger">{actionError}</Alert>}

      <Row className="g-3 mb-3">
        {tiles.map((tile) => (
          <Col key={tile.label} xs={6} md={4} xl={2}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted small text-uppercase">{tile.label}</div>
                <div className="fs-4 fw-semibold">{tile.value}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Receiving lives on the scan desk (View Orders State 6) — counting here ended at requests nobody could commit (SSD-6208) [BR-52]. */}
      <Alert variant="info" role="note" className="mb-3">
        Counting, shelving and confirmation happen at the{' '}
        <Alert.Link as={Link} to={ROUTES.fulfillment.viewOrders}>
          View Orders scan desk
        </Alert.Link>{' '}
        — scan waybill{' '}
        <span className="font-monospace">
          {waybills[0] ?? doc.invoiceNo ?? '(register one on the request list first)'}
        </span>{' '}
        there to open this request for receiving.
      </Alert>

      {/* The hub's search results and Slack's @mention links land here, so the thread they point at is shown here.
          Read-only: notes are written from the request list's 💬 Comments pane. */}
      <Card className="mb-3">
        <Card.Header className="fw-semibold">Comments ({comments.length})</Card.Header>
        <Card.Body>
          <InboundCommentThread comments={comments} label={`Comments on ${doc.inboundNo}`} />
          <div className="text-muted small mt-2">
            Notes are written with the 💬 Comments button on the{' '}
            <Link to={ROUTES.fulfillment.inboundRequests}>Request list</Link>.
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Lines ({lines.length})</Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-dark">
              <tr>
                <th>SKU</th>
                <th className="text-end">Expected</th>
                <th className="text-end">Inbound</th>
                <th className="text-end">Unit Cost</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.lineNo}>
                  <td className="font-monospace">
                    {line.sku}
                    {line.isFoc && (
                      <Badge bg="info" className="ms-2">
                        FOC
                      </Badge>
                    )}
                    {line.isNewSku && (
                      <Badge bg="dark" className="ms-2">
                        New
                      </Badge>
                    )}
                    {/* Nobody typed this code, so the shelf guard never saw it — the desk has to notice before confirming. */}
                    {line.landsOnVirtualLocation && (
                      <Badge
                        bg="warning"
                        text="dark"
                        className="ms-2"
                        title="Confirming books this line into a bookkeeping code; assign a real shelf first"
                        aria-label="Off-shelf: confirming books this line into a bookkeeping code; assign a real shelf first"
                      >
                        Off-shelf
                      </Badge>
                    )}
                  </td>
                  <td className="text-end">{line.qtyExpected}</td>
                  <td className={`text-end${line.qtyReceived > line.qtyExpected ? ' text-danger fw-semibold' : ''}`}>
                    {line.qtyReceived}
                  </td>
                  <td className="text-end">{line.unitCost ?? '—'}</td>
                  <td className="font-monospace">
                    {line.locationCode ??
                      (line.suggestedLocationCode ? (
                        `${line.suggestedLocationCode} (suggested)`
                      ) : (
                        <span className="text-muted">—</span>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}
